import React, { useRef, useState } from "react";
import { Box, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import { motion, useInView, useReducedMotion } from "framer-motion";
import SectionHeading from "./SectionHeading";
import { useThemeContext } from "../../context/themeContext";

/**
 * The homepage's Track feature: an annotated screenshot beside the heading
 * and a run-down of what tracking offers. The screenshot fades in growing
 * slightly — just after the heading assembles where they sit side by side,
 * or on its own scroll on mobile, where it sits below the text.
 */
export default function TrackSection() {
  const { mode } = useThemeContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const reduce = useReducedMotion();
  const imageRef = useRef(null);
  const imageInView = useInView(imageRef, {
    once: true,
    margin: "0px 0px -20% 0px",
  });
  const [headingAssembled, setHeadingAssembled] = useState(false);
  const showImage = isMobile ? imageInView : headingAssembled;

  return (
    <Grid
      container
      spacing={{ xs: 1, md: 5 }}
      sx={{
        maxWidth: "lg",
        pt: { xs: 2, md: 1 },
        alignItems: "center",
      }}
    >
      <Grid
        item
        xs={12}
        sm={7}
        md={8}
        sx={{
          order: { xs: 2, sm: 1 },
          display: "flex",
          alignItems: "flex-end",
          mt: { xs: 0, md: 2 },
        }}
      >
        <Box
          ref={imageRef}
          component={motion.img}
          initial={reduce ? false : { opacity: 0, scale: 0.96 }}
          animate={showImage || reduce ? { opacity: 1, scale: 1 } : undefined}
          // Lands as the heading's pieces settle, rather than with them
          transition={{
            duration: 0.7,
            delay: isMobile ? 0 : 0.3,
            ease: "easeOut",
          }}
          src={`/images/feature-graphic-${
            mode === "light" ? "dark" : "light"
          }.png`}
          alt={"Graphic showcasing the tracking feature of Curate"}
          sx={{
            width: "100%",
            display: "block",
          }}
        />
      </Grid>
      <Grid
        item
        xs={12}
        sm={5}
        md={4}
        sx={{
          order: {
            xs: 1,
            sm: 2,
          },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: { xs: "center", sm: "flex-start" },
        }}
      >
        <SectionHeading
          icon="track"
          onAssemble={() => setHeadingAssembled(true)}
          title=<b>Track</b>
          justifyContent={{ xs: "center", sm: "flex-start" }}
        />

        <Box
          maxWidth="250px"
          sx={{
            textAlign: "left",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            mt: 0,
            mb: 2,
          }}
        >
          <Typography sx={{ mb: 1.5 }}>
            Track movies, books and TV shows together in one place. No separate
            apps.
          </Typography>
          <Typography sx={{ mb: 1.5 }}>
            Simple, intuitive tracking: queue it, start it, finish it (or stop
            it).
          </Typography>
          <Typography sx={{ mb: 1.5 }}>
            Minimalist, ad-free design creates a quiet, personal space for your
            taste.
          </Typography>
          <Typography sx={{ mb: 1.5 }}>
            Optionally add notes, ratings and reactions. Share notes with
            friends, or keep private.
          </Typography>
          <Typography sx={{ mb: 1.5 }}>
            Already tracking elsewhere? Import from Letterboxd, Goodreads,
            StoryGraph, Trakt and TV Time. More coming soon.
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
}
